import React, { useState, useCallback, useMemo, createContext, useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { PagingContext } from '../../../contexts/paging-context';
import { LogService } from '../../../services/log-service';
import Global from '../../../store/global-selectors';
import { getPurchaseOrders } from './api/purchase-order-api';
import { transformFiltersToApiRequest } from './data/helpers/transform-filters';
import { useSuppliers } from './hooks/use-suppliers';

/**
 * @typedef {import('../../../store/purchase-orders/purchase-orders.types').SortState} SortState
 */

/**
 * @typedef {import('../../../store/purchase-orders/purchase-orders.types').FilterBarFilter} FilterBarFilter
 */

/**
 * @typedef {object} PurchaseOrdersGridContextValue
 * @property {FilterBarFilter[]} appliedFilters - Currently applied filters
 * @property {(filters: FilterBarFilter[]) => void} onFiltersChanged - Handler for filter changes
 * @property {() => void} clearFilters - Handler to clear all filters
 * @property {string[]} selectedIds - Currently selected purchase order IDs
 * @property {(ids: string[]) => void} setSelectedIds - Handler to update selected IDs
 * @property {SortState} sortState - Current sorting state
 * @property {(sortState: SortState) => void} onSortChanged - Handler for sort changes
 * @property {Array<import('./api/purchase-order-api').PurchaseOrderRow>} data - Purchase order grid data
 * @property {boolean} isLoading - Loading state for purchase orders
 * @property {number} totalRecords - Total number of purchase orders
 * @property {() => Promise<void>} refreshPurchaseOrders - Function to refresh purchase orders
 * @property {Array<{label: string, value: string}>} suppliers - List of suppliers for dropdowns
 * @property {boolean} isLoadingSuppliers - Loading state for suppliers
 * @property {() => void} refetchSuppliers - Function to refetch suppliers
 */

const PurchaseOrdersGridContext = createContext(/** @type {PurchaseOrdersGridContextValue | null} */ (null));

/**
 * Provider component for Purchase Orders Grid context
 * Manages filter state, sort state, data fetching, and selection for purchase orders grid
 * @param {{children: React.ReactNode}} props
 * @returns {JSX.Element}
 */
export const PurchaseOrdersGridContextProvider = ({ children }) => {
  const [appliedFilters, setAppliedFilters] = useState(/** @type {Array<object>} */ (emptyArray));
  const [sortState, setSortState] = useState(/** @type {SortState} */ ({ orderBy: null, direction: null }));
  const [selectedIds, setSelectedIds] = useState(/** @type {string[]} */ (emptyArray));
  const [data, setGridData] = useState(/** @type {Array<import('./api/purchase-order-api').PurchaseOrderRow>} */ (emptyArray));
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  const currentRequestRef = useRef();
  const pagingContext = useContext(PagingContext);

  if (!pagingContext) {
    throw new Error('PurchaseOrdersGridContextProvider must be used within PagingContextProvider');
  }

  const { page, pageSize, updatePageInformation } = pagingContext;
  const warehouses = useSelector(Global.selectSellerInventoryWarehouses);
  const { suppliers, isLoading: isLoadingSuppliers, refetch: refetchSuppliers } = useSuppliers();

  const warehouseLookup = useMemo(() => {
    return warehouses.reduce((map, warehouse) => {
      const key = warehouse.inventoryWarehouseId;
      if (key !== null && key !== undefined) {
        return { ...map, [key]: warehouse.name ?? '' };
      }
      return map;
    }, emptyObject);
  }, [warehouses]);

  const fetchPurchaseOrders = useCallback(() => {
    if (currentRequestRef.current) {
      currentRequestRef.current.cancel();
    }

    setIsLoading(true);
    const requestBody = {
      ...transformFiltersToApiRequest(appliedFilters),
    };

    if (sortState.orderBy && sortState.direction) {
      requestBody.orderBy = sortState.orderBy;
      requestBody.direction = sortState.direction;
    }

    const purchaseOrdersPromise = getPurchaseOrders(requestBody, page, pageSize);

    currentRequestRef.current = purchaseOrdersPromise;

    purchaseOrdersPromise.promise
      .then(({ purchaseOrders, totalCount = 0 }) => {
        setGridData(
          purchaseOrders.map((order) => {
            const { warehouseId } = order;
            return {
              ...order,
              warehouseName: warehouseLookup[warehouseId] ?? '',
              id: order.purchaseOrderId?.toString() || '',
            };
          }),
        );
        setTotalRecords(totalCount);
      })
      .catch((error) => {
        LogService.logError(new Error('Failed to fetch purchase orders', { cause: error }));
        setGridData(emptyArray);
        setTotalRecords(0);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [warehouseLookup, appliedFilters, page, pageSize, sortState]);

  useEffect(() => {
    fetchPurchaseOrders();

    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.cancel();
      }
    };
  }, [fetchPurchaseOrders]);

  const onFiltersChanged = useCallback(
    (filters) => {
      setAppliedFilters(filters);
      updatePageInformation(1, pageSize);
    },
    [pageSize, updatePageInformation],
  );

  const clearFilters = useCallback(() => {
    setAppliedFilters(emptyArray);
    updatePageInformation(1, pageSize);
  }, [pageSize, updatePageInformation]);

  const onSortChanged = useCallback(({ orderBy, direction }) => {
    setSortState({ orderBy, direction });
  }, []);

  const contextValue = useMemo(
    () => ({
      appliedFilters,
      onFiltersChanged,
      clearFilters,
      selectedIds,
      setSelectedIds,
      sortState,
      onSortChanged,
      data,
      isLoading,
      totalRecords,
      refreshPurchaseOrders: fetchPurchaseOrders,
      suppliers,
      isLoadingSuppliers,
      refetchSuppliers,
    }),
    [
      appliedFilters,
      onFiltersChanged,
      clearFilters,
      selectedIds,
      data,
      isLoading,
      totalRecords,
      fetchPurchaseOrders,
      sortState,
      onSortChanged,
      suppliers,
      isLoadingSuppliers,
      refetchSuppliers,
    ],
  );

  return <PurchaseOrdersGridContext.Provider value={contextValue}>{children}</PurchaseOrdersGridContext.Provider>;
};

/**
 * Hook to access Purchase Orders Grid context
 * @returns {PurchaseOrdersGridContextValue} The context value
 */
export const usePurchaseOrdersGridContext = () => {
  const context = useContext(PurchaseOrdersGridContext);
  if (!context) {
    throw new Error('usePurchaseOrdersGridContext must be used within PurchaseOrdersGridContextProvider');
  }
  return context;
};
